import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';

const SEND_ON_LABELS = {
  'first-of-month': '1st of Month',
  'last-of-month':  'Last of Month',
  'custom-day':     'Custom Day',
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const BLANK_FORM = {
  group_id:     '',
  send_on:      'first-of-month',
  custom_day:   '',
  months_ahead: 2,
  subject:      '',
  is_active:    true,
};

function CalendarEmailSchedule() {
  const { getAuthHeader } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [schRes, grpRes, logRes] = await Promise.all([
        fetch(`${API_URL}/api/calendar/email-schedules`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/user-groups`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/calendar/email-log?limit=20`, { headers: getAuthHeader() }),
      ]);
      const [schData, grpData, logData] = await Promise.all([schRes.json(), grpRes.json(), logRes.json()]);
      setSchedules(schData.data || []);
      setGroups(grpData.groups || []);
      setLog(logData.data || []);
    } catch {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(BLANK_FORM);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (schedule) => {
    setEditingId(schedule.id);
    setForm({
      group_id:     String(schedule.group_id),
      send_on:      schedule.send_on,
      custom_day:   schedule.custom_day != null ? String(schedule.custom_day) : '',
      months_ahead: schedule.months_ahead,
      subject:      schedule.subject || '',
      is_active:    schedule.is_active,
    });
    setError(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK_FORM);
    setError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const payload = {
      group_id:     parseInt(form.group_id, 10),
      send_on:      form.send_on,
      custom_day:   form.send_on === 'custom-day' ? parseInt(form.custom_day, 10) : null,
      months_ahead: parseInt(form.months_ahead, 10),
      subject:      form.subject.trim() || null,
      is_active:    form.is_active,
    };

    try {
      const url = editingId
        ? `${API_URL}/api/calendar/email-schedules/${editingId}`
        : `${API_URL}/api/calendar/email-schedules`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed.'); return; }

      cancelForm();
      fetchAll();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      const res = await fetch(`${API_URL}/api/calendar/email-schedules/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      if (res.ok) fetchAll();
    } catch {
      setError('Delete failed.');
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      await fetch(`${API_URL}/api/calendar/email-schedules/${schedule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ is_active: !schedule.is_active }),
      });
      fetchAll();
    } catch {
      setError('Update failed.');
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Calendar Email Schedules</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={14} style={{ marginRight: 4 }} /> New Schedule
          </button>
        )}
      </div>

      {error && (
        <div className="approval-modal-error" style={{ marginBottom: 16 }}>{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleSave} style={{ background: '#f9f9f9', border: '1px solid #ddd', borderRadius: 8, padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>{editingId ? 'Edit Schedule' : 'New Schedule'}</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Group *</label>
              <select required value={form.group_id} onChange={e => setForm({ ...form, group_id: e.target.value })}>
                <option value="">Select a group...</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Send On *</label>
              <select value={form.send_on} onChange={e => setForm({ ...form, send_on: e.target.value, custom_day: '' })}>
                <option value="first-of-month">1st of Month</option>
                <option value="last-of-month">Last of Month</option>
                <option value="custom-day">Custom Day</option>
              </select>
            </div>
          </div>

          {form.send_on === 'custom-day' && (
            <div className="form-row">
              <div className="form-group">
                <label>Day of Month (1–28) *</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  required
                  value={form.custom_day}
                  onChange={e => setForm({ ...form, custom_day: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Months Ahead (1–11) *</label>
              <input
                type="number"
                min="1"
                max="11"
                required
                value={form.months_ahead}
                onChange={e => setForm({ ...form, months_ahead: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Subject (optional)</label>
              <input
                type="text"
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                placeholder="Default: Papa's Palace — Upcoming Reservations"
              />
            </div>
          </div>

          <div className="form-row" style={{ alignItems: 'center', gap: 8 }}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => setForm({ ...form, is_active: e.target.checked })}
              />
              Active
            </label>
          </div>

          {error && <div className="approval-modal-error" style={{ marginBottom: 8 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="submit" className="btn btn-success" disabled={saving}>
              {saving ? 'Saving...' : <><Check size={14} /> Save</>}
            </button>
            <button type="button" className="btn btn-neutral" onClick={cancelForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div style={{ marginBottom: 32 }}>
        {schedules.length === 0 ? (
          <p style={{ color: '#888' }}>No schedules configured. Create one to enable automatic calendar emails.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f0f0f0', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Group</th>
                <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Send On</th>
                <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Months</th>
                <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Subject</th>
                <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Active</th>
                <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px 12px' }}>{s.group_name || s.group_id}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {SEND_ON_LABELS[s.send_on]}
                    {s.send_on === 'custom-day' && s.custom_day != null && ` (day ${s.custom_day})`}
                  </td>
                  <td style={{ padding: '10px 12px' }}>{s.months_ahead}</td>
                  <td style={{ padding: '10px 12px', color: s.subject ? '#333' : '#aaa', fontStyle: s.subject ? 'normal' : 'italic' }}>
                    {s.subject || 'default'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      className={`btn btn-sm ${s.is_active ? 'btn-success' : 'btn-neutral'}`}
                      onClick={() => handleToggleActive(s)}
                    >
                      {s.is_active ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => openEdit(s)} title="Edit">
                      <Edit2 size={13} />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(s.id)} title="Delete">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <h3 style={{ marginBottom: 12 }}>Recent Sends</h3>
        {log.length === 0 ? (
          <p style={{ color: '#888' }}>No emails sent yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f0f0f0', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px', borderBottom: '2px solid #ddd' }}>Date</th>
                <th style={{ padding: '8px 10px', borderBottom: '2px solid #ddd' }}>Trigger</th>
                <th style={{ padding: '8px 10px', borderBottom: '2px solid #ddd' }}>Range</th>
                <th style={{ padding: '8px 10px', borderBottom: '2px solid #ddd' }}>Recipients</th>
                <th style={{ padding: '8px 10px', borderBottom: '2px solid #ddd' }}>Sent By</th>
              </tr>
            </thead>
            <tbody>
              {log.map(entry => (
                <tr key={entry.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 10px' }}>
                    {new Date(entry.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ background: entry.trigger_type === 'manual' ? '#dbeafe' : '#dcfce7', color: entry.trigger_type === 'manual' ? '#1d4ed8' : '#166534', padding: '2px 7px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                      {entry.trigger_type}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    {MONTH_NAMES[entry.start_month]} – {MONTH_NAMES[entry.end_month]} {entry.year}
                  </td>
                  <td style={{ padding: '8px 10px' }}>{entry.recipient_count}</td>
                  <td style={{ padding: '8px 10px', color: '#666' }}>
                    {entry.triggered_by_name || (entry.trigger_type === 'scheduled' ? 'Cron' : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CalendarEmailSchedule;
